<template>
	<div class="animated fadeIn analytics-report">

		<!-- Header -->
		<div class="d-flex flex-wrap align-items-center mb-3">
			<div class="mr-auto">
				<h5 class="mb-0"><i class="icon-docs mr-2"></i>GAD Analytics Report</h5>
				<small class="text-muted">
					The same growth data as the dashboard, as tables you can read and export
					<span v-if="latestDate" class="ml-1">· latest snapshot <strong>{{ latestDate }}</strong></span>
				</small>
			</div>
			<div class="d-flex align-items-center mt-2 mt-md-0">
				<b-form-select v-model="days" :options="dayOptions" size="sm" class="range-select mr-2"
					:disabled="loading" @change="load"></b-form-select>
				<b-button size="sm" variant="outline-secondary" class="mr-2" @click="load" :disabled="loading">
					<i class="icon-refresh" :class="{ spin: loading }"></i> Refresh
				</b-button>
				<b-button size="sm" variant="primary" @click="exportAll" :disabled="loading || !hasData">
					<i class="fa fa-download mr-1"></i> Export CSV
				</b-button>
			</div>
		</div>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<b-alert v-else-if="unavailable" show variant="warning">
			<strong>Growth analytics unavailable.</strong>
			{{ unavailableMessage || 'The GAD database could not be reached.' }}
		</b-alert>

		<div v-else>
			<!-- Totals -->
			<b-row class="mb-3">
				<b-col sm="6" md="3" class="mb-2" v-for="t in totals" :key="t.label">
					<div class="stat-card">
						<div class="stat-num">{{ formatNumber(t.value) }}</div>
						<div class="stat-label">{{ t.label }}</div>
					</div>
				</b-col>
			</b-row>

			<!-- Growth by day -->
			<b-card no-body class="mb-3">
				<div slot="header" class="d-flex align-items-center">
					<span><i class="icon-graph mr-2"></i>Growth by day</span>
					<small class="text-muted ml-2">last {{ days }} days</small>
					<b-button size="sm" variant="link" class="ml-auto p-0"
						@click="exportSection('growth-by-day', growthColumns, growthByDay)"
						:disabled="!growthByDay.length">Export</b-button>
				</div>
				<div class="table-scroll">
					<table class="table table-sm table-striped table-hover mb-0">
						<thead>
							<tr>
								<th v-for="c in growthColumns" :key="c.key" :class="c.numeric ? 'text-right' : ''">{{ c.label }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, i) in growthByDay" :key="i">
								<td v-for="c in growthColumns" :key="c.key" :class="c.numeric ? 'text-right num' : ''">
									{{ c.numeric ? formatNumber(row[c.key]) : row[c.key] }}
								</td>
							</tr>
							<tr v-if="!growthByDay.length"><td :colspan="growthColumns.length" class="text-center text-muted py-3">No rows in this range.</td></tr>
						</tbody>
					</table>
				</div>
			</b-card>

			<b-row>
				<b-col md="6" class="mb-3">
					<report-table
						title="Acquisition by month"
						icon="icon-calendar"
						:columns="acquisitionColumns"
						:rows="acquisitionByMonth"
						@export="exportSection('acquisition-by-month', acquisitionColumns, acquisitionByMonth)"/>
				</b-col>
				<b-col md="6" class="mb-3">
					<report-table
						title="Top resellers"
						icon="icon-people"
						:columns="resellerColumns"
						:rows="topResellers"
						@export="exportSection('top-resellers', resellerColumns, topResellers)"/>
				</b-col>
			</b-row>

			<b-row>
				<b-col md="6" class="mb-3">
					<report-table
						title="Clients by sales channel"
						icon="icon-share-alt"
						:columns="channelColumns"
						:rows="channelBreakdown"
						@export="exportSection('sales-channels', channelColumns, channelBreakdown)"/>
				</b-col>
				<b-col md="6" class="mb-3">
					<report-table
						title="Package tiers"
						icon="icon-layers"
						:columns="tierColumns"
						:rows="packageTiers"
						@export="exportSection('package-tiers', tierColumns, packageTiers)"/>
				</b-col>
			</b-row>
		</div>
	</div>
</template>

<script>
import analyticsService from '@/services/analyticsService';
import toastService from '@/services/toastService';

/**
 * Small presentational table used for the four side-by-side breakdowns. Local to this page
 * rather than in src/components — nothing else needs it yet.
 */
const ReportTable = {
	name: 'ReportTable',
	props: {
		title: String,
		icon: {type: String, default: 'icon-list'},
		columns: {type: Array, default: () => []},
		rows: {type: Array, default: () => []}
	},
	methods: {
		fmt(value, numeric) {
			if (!numeric) return value;
			const n = Number(value);
			return isNaN(n) ? value : n.toLocaleString();
		}
	},
	template: `
		<b-card no-body class="h-100">
			<div slot="header" class="d-flex align-items-center">
				<span><i :class="icon" class="mr-2"></i>{{ title }}</span>
				<b-button size="sm" variant="link" class="ml-auto p-0"
					@click="$emit('export')" :disabled="!rows.length">Export</b-button>
			</div>
			<div class="table-scroll">
				<table class="table table-sm table-striped table-hover mb-0">
					<thead>
						<tr><th v-for="c in columns" :key="c.key" :class="c.numeric ? 'text-right' : ''">{{ c.label }}</th></tr>
					</thead>
					<tbody>
						<tr v-for="(row, i) in rows" :key="i">
							<td v-for="c in columns" :key="c.key" :class="c.numeric ? 'text-right num' : ''">{{ fmt(row[c.key], c.numeric) }}</td>
						</tr>
						<tr v-if="!rows.length"><td :colspan="columns.length" class="text-center text-muted py-3">No data.</td></tr>
					</tbody>
				</table>
			</div>
		</b-card>
	`
};

export default {
	name: 'AnalyticsReport',
	components: {ReportTable},

	data() {
		return {
			loading: false,
			unavailable: false,
			unavailableMessage: '',
			days: 30,
			dayOptions: [
				{text: 'Last 7 days', value: 7},
				{text: 'Last 30 days', value: 30},
				{text: 'Last 90 days', value: 90},
				{text: 'Last 180 days', value: 180},
				{text: 'Last 365 days', value: 365}
			],
			summary: {},
			growthByDay: [],
			acquisitionByMonth: [],
			topResellers: [],
			channelBreakdown: [],
			packageTiers: []
		};
	},

	computed: {
		hasData() {
			return this.growthByDay.length || this.acquisitionByMonth.length ||
				this.topResellers.length || this.channelBreakdown.length || this.packageTiers.length;
		},
		latestDate() {
			if (!this.growthByDay.length) return null;
			const row = this.growthByDay[0];
			return row.date || row.day || null;
		},
		totals() {
			return [
				{label: 'Clients', value: this.summary.totalClients},
				{label: 'Devices', value: this.summary.totalDevices},
				{label: 'Active devices', value: this.summary.totalActiveDevices},
				{label: 'Employees', value: this.summary.totalEmployees}
			];
		},
		// Columns are derived from the first row so the table follows whatever the GAD query
		// returns, rather than hard-coding names that could drift.
		growthColumns() { return this.columnsFrom(this.growthByDay, ['date', 'day']); },
		acquisitionColumns() { return this.columnsFrom(this.acquisitionByMonth, ['month']); },
		resellerColumns() { return this.columnsFrom(this.topResellers, ['reseller_name', 'resellerName']); },
		channelColumns() { return this.columnsFrom(this.channelBreakdown, ['sales_channel', 'salesChannel']); },
		tierColumns() { return this.columnsFrom(this.packageTiers, ['version_name', 'versionName']); }
	},

	methods: {
		load() {
			this.loading = true;
			this.unavailable = false;
			analyticsService.getSummary(this.days, (data) => {
				this.loading = false;
				// Same marker the dashboard checks: the endpoint degrades to {available:false,…}
				// rather than erroring when GAD is unreachable.
				if (!data || data.available === false) {
					this.unavailable = true;
					this.unavailableMessage = (data && data.error) || '';
					return;
				}
				this.summary = data;
				this.growthByDay = data.growthByDay || [];
				this.acquisitionByMonth = data.acquisitionByMonth || [];
				this.topResellers = data.topResellers || [];
				this.channelBreakdown = data.channelBreakdown || [];
				this.packageTiers = data.packageTiers || [];
			}, (err) => {
				this.loading = false;
				this.unavailable = true;
				this.unavailableMessage = err || '';
			});
		},

		/** Builds column defs from the first row; anything not in `labelKeys` is treated as numeric. */
		columnsFrom(rows, labelKeys) {
			if (!rows || !rows.length) return [];
			return Object.keys(rows[0]).map(key => ({
				key: key,
				label: this.humanise(key),
				numeric: labelKeys.indexOf(key) === -1
			}));
		},

		humanise(key) {
			const spaced = String(key).replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
			return spaced.charAt(0).toUpperCase() + spaced.slice(1);
		},

		formatNumber(value) {
			if (value === null || value === undefined || value === '') return '—';
			const n = Number(value);
			return isNaN(n) ? value : n.toLocaleString();
		},

		exportSection(name, columns, rows) {
			if (!rows || !rows.length) return;
			const header = columns.map(c => c.label);
			const body = rows.map(r => columns.map(c => r[c.key]));
			downloadCsv(name + '-' + this.days + 'd', [header].concat(body));
			toastService.showSuccessToast('Exported ' + rows.length + ' rows');
		},

		/** One file containing every section, each under its own heading. */
		exportAll() {
			const lines = [];
			const push = (title, columns, rows) => {
				if (!rows || !rows.length) return;
				lines.push([title]);
				lines.push(columns.map(c => c.label));
				rows.forEach(r => lines.push(columns.map(c => r[c.key])));
				lines.push([]);
			};
			lines.push(['GAD Analytics Report']);
			lines.push(['Range', 'last ' + this.days + ' days']);
			lines.push([]);
			lines.push(['Totals']);
			this.totals.forEach(t => lines.push([t.label, t.value]));
			lines.push([]);
			push('Growth by day', this.growthColumns, this.growthByDay);
			push('Acquisition by month', this.acquisitionColumns, this.acquisitionByMonth);
			push('Top resellers', this.resellerColumns, this.topResellers);
			push('Clients by sales channel', this.channelColumns, this.channelBreakdown);
			push('Package tiers', this.tierColumns, this.packageTiers);
			downloadCsv('gad-analytics-report-' + this.days + 'd', lines);
			toastService.showSuccessToast('Report exported');
		}
	},

	created() {
		this.load();
	}
};

function csvCell(value) {
	if (value === null || value === undefined) return '';
	const s = String(value);
	// Quote anything containing a delimiter, quote or newline; double up embedded quotes.
	return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function downloadCsv(filename, rows) {
	const csv = rows.map(r => r.map(csvCell).join(',')).join('\r\n');
	// BOM so Excel opens UTF-8 correctly.
	const blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8;'});
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename + '.csv';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}
</script>

<style scoped>
.range-select {
	width: auto;
}

.stat-card {
	background: #fff;
	border: 1px solid #e4e7ea;
	border-radius: 4px;
	padding: 0.75rem 1rem;
}

.stat-num {
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.2;
	font-variant-numeric: tabular-nums;
}

.stat-label {
	font-size: 0.75rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #73818f;
}

.table-scroll {
	max-height: 24rem;
	overflow-y: auto;
	overflow-x: auto;
}

/* border-collapse hides sticky header borders once stuck — same fix as CustomerModules. */
.table-scroll table {
	border-collapse: separate;
	border-spacing: 0;
}

.table-scroll thead th {
	position: sticky;
	top: 0;
	z-index: 2;
	background: #fff;
	border-top: 1px solid #e4e7ea;
	border-bottom: 1px solid #e4e7ea;
	white-space: nowrap;
}

.num {
	font-variant-numeric: tabular-nums;
}

.spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}
</style>
